/**
 * get-file-information — Get comprehensive file info in browser and Node.js.
 *
 * @example Browser
 * ```ts
 * import { getFileInfo } from 'get-file-information'
 * const info = await getFileInfo(input.files[0])
 * ```
 *
 * @example Node.js
 * ```ts
 * import { getFileInfo } from 'get-file-information'
 * const info = await getFileInfo('/path/to/file.jpg')
 * ```
 */

import type { FileInfo, NodeFileInfo, MediaDimensions, HashAlgorithm } from './types'
import { formatSize, formatTime, getExtension, getStem, getBaseName, inferMimeType } from './core'
import { getFileInfoFromBrowser } from './browser'
import { getFileInfoFromNode } from './node'

/**
 * Detect if running in a browser environment.
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.document !== 'undefined'
}

/**
 * Get comprehensive file information.
 *
 * - **Browser**: pass a `File` object (from `<input type="file">` or drag-and-drop)
 * - **Node.js**: pass a file path string
 */
export async function getFileInfo(input: File): Promise<FileInfo>
export async function getFileInfo(input: string): Promise<NodeFileInfo>
export async function getFileInfo(input: File | string): Promise<FileInfo | NodeFileInfo> {
  if (isBrowser()) {
    if (input instanceof File) {
      return getFileInfoFromBrowser(input)
    }
    throw new TypeError(
      'In browser environment, getFileInfo expects a File object. ' +
        'Use getFileInfo(file) where file comes from an <input type="file">.',
    )
  }

  // Node.js path
  if (typeof input === 'string') {
    return getFileInfoFromNode(input)
  }

  throw new TypeError(
    'In Node.js environment, getFileInfo expects a file path string. ' +
      'Use getFileInfo("/path/to/file").',
  )
}

/**
 * Check if running in browser.
 */
export function isRunningInBrowser(): boolean {
  return isBrowser()
}

// Re-export types
export type { FileInfo, NodeFileInfo, MediaDimensions, HashAlgorithm }

// Re-export pure utility functions (work in any environment)
export { formatSize, formatTime, getExtension, getStem, getBaseName, inferMimeType }
